import React, { Component, createRef } from "react";

// The wizard floats on the same ground, inside the same haloed sheet, as /login, /join and the school wizard, so it
// reuses their stylesheets and only adds what is particular to a gym account.
import "../Join/Join.scss";
import "../Onboarding/Onboarding.scss";
import "./GymOnboarding.scss";

import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";

import { apiFetch, platformInfo, isLoggedIn } from "../../Helper/Api";
import { t } from "../../Helper/i18n";

const properties = require("../../properties.json");
const LOGO = require("../../assets/img/logo copy.png").default;

export const STEPS = [
  { id: 1, key: "gym.setup.step.link" },
  { id: 2, key: "gym.setup.step.mode" },
  { id: 3, key: "gym.setup.step.plan" },
  { id: 4, key: "gym.setup.step.program" },
];

/** The step a coach resumes at, from what is already done (see OnboardingStatusResponse). */
export function stepFor(status) {
  if (!status || !status.subdomain) return 1;
  if (!status.teamMode) return 2;
  if (!status.plansCount) return 3;
  return 4;
}

const EMPTY_PLAN = { name: "", price: "", intervalMonths: 1, trialDays: 0 };

/** First set-up of a gym / fitness coach: link, solo or team, first plan, first program. Only the link and the first
 *  plan are needed; the last step can wait. */
class GymOnboarding extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
    this.thread = null;
    this.checkTimer = null;
  }

  state = {
    loading: true,
    step: 1,
    baseDomain: "",
    subdomain: "",
    subdomainAvailable: null,
    subdomainChecking: false,
    saving: false,
    mode: null,
    plan: { ...EMPTY_PLAN },
    plansDone: 0,
  };

  componentDidMount() {
    document.title = properties.site_name + " | " + t("gym.setup.title");
    if (!isLoggedIn()) {
      window.location.href = "/";
      return;
    }
    platformInfo()
      .then((info) => this.setState({ baseDomain: (info && info.baseDomain) || "" }))
      .catch(() => {});
    apiFetch("/teachers/me/onboarding-status")
      .then((status) => {
        if (status.complete && status.programsCount > 0) {
          window.location.href = "/welcome";
          return;
        }
        this.setState({
          loading: false,
          step: status.complete ? 4 : stepFor(status),
          subdomain: status.subdomain || "",
          mode: status.teamMode || null,
          plansDone: status.plansCount || 0,
        });
      })
      .catch(() => this.setState({ loading: false }));
  }

  componentDidUpdate() {
    if (this.thread) this.thread.scrollTop = this.thread.scrollHeight;
  }

  say = (severity, summary, detail) => this.toast.current && this.toast.current.show({ severity, summary, detail });
  fail = (e) => this.say("error", t("common.alert"), (e && e.message) || t("common.failed"));
  go = (step) => this.setState({ step, saving: false });
  finish = () => {
    window.location.href = "/welcome";
  };

  // ---- 1: the link ----
  onSubdomainChange = (value) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    this.setState({ subdomain: clean, subdomainAvailable: null });
    clearTimeout(this.checkTimer);
    if (clean.length < 3) return;
    this.checkTimer = setTimeout(() => {
      this.setState({ subdomainChecking: true });
      apiFetch("/teachers/subdomain-available", { query: { value: clean } })
        .then((res) => this.setState({ subdomainAvailable: res.available, subdomainChecking: false }))
        .catch(() => this.setState({ subdomainChecking: false }));
    }, 400);
  };

  saveSubdomain = () => {
    const { subdomain } = this.state;
    if (subdomain.length < 3) {
      this.say("error", t("common.alert"), t("gym.setup.linkShort"));
      return;
    }
    this.setState({ saving: true });
    apiFetch("/teachers/me/subdomain", { method: "PUT", body: { subdomain } })
      .then(() => this.go(2))
      .catch((e) => {
        this.setState({ saving: false });
        this.fail(e);
      });
  };

  // ---- 2: solo or team ----
  chooseMode = (mode) => {
    this.setState({ saving: true });
    apiFetch("/instructors/team-mode", { method: "PUT", body: { mode } })
      .then(() => {
        this.setState({ mode });
        this.go(3);
      })
      .catch((e) => {
        this.setState({ saving: false });
        this.fail(e);
      });
  };

  // ---- 3: the first plan ----
  setPlan = (patch) => this.setState({ plan: { ...this.state.plan, ...patch } });

  savePlan = () => {
    const { plan } = this.state;
    const price = Number(plan.price);
    if (!plan.name.trim()) {
      this.say("error", t("common.alert"), t("gym.plans.nameRequired"));
      return;
    }
    if (!(price >= 0) || plan.price === "") {
      this.say("error", t("common.alert"), t("gym.setup.priceRequired"));
      return;
    }
    this.setState({ saving: true });
    apiFetch("/plans", {
      method: "POST",
      body: {
        name: plan.name.trim(),
        price,
        currency: "EGP",
        intervalMonths: plan.intervalMonths,
        trialDays: Math.max(0, Math.min(60, Number(plan.trialDays) || 0)),
        enabled: true,
      },
    })
      .then(() => {
        this.setState({ plansDone: this.state.plansDone + 1, plan: { ...EMPTY_PLAN } });
        this.go(4);
      })
      .catch((e) => {
        this.setState({ saving: false });
        this.fail(e);
      });
  };

  renderThread = (children) => (
    <div className="ob-thread" ref={(el) => (this.thread = el)}>
      <div className="ob-stream">{children}</div>
    </div>
  );

  renderLink() {
    const { subdomain, subdomainAvailable, subdomainChecking, saving, baseDomain } = this.state;
    const domain = baseDomain || "igma-it.com";
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>{t("gym.setup.linkTitle")}</b>
              <p>{t("gym.setup.linkText")}</p>
            </div>
            {subdomain.length > 0 && (
              <div className="bubble me link" style={{ "--d": "120ms" }}>
                <span className="txt" dir="ltr">{subdomain}.{domain}</span>
              </div>
            )}
            <div className={`bubble sys state ${subdomainAvailable === true ? "ok" : subdomainAvailable === false ? "bad" : ""}`} style={{ "--d": "160ms" }}>
              {subdomainChecking && <span className="checking"><i className="pi pi-spin pi-spinner"></i> {t("gym.setup.checking")}</span>}
              {!subdomainChecking && subdomainAvailable === true && <span className="available">✓ {t("gym.setup.available")}</span>}
              {!subdomainChecking && subdomainAvailable === false && <span className="unavailable">✗ {t("gym.setup.taken")}</span>}
              {!subdomainChecking && subdomainAvailable === null && <span className="idle">{t("gym.setup.linkHint")}</span>}
            </div>
          </>
        )}
        <div className="ob-composer">
          <div className={`ob-write domain ${subdomainAvailable === true ? "ok" : subdomainAvailable === false ? "bad" : ""}`}>
            <InputText autoFocus value={subdomain} placeholder="coach-karim" onChange={(e) => this.onSubdomainChange(e.target.value)} />
            <span className="suffix">.{domain}</span>
          </div>
          <Button
            className="next-button activate"
            label={saving ? t("gym.setup.saving") : t("gym.setup.next")}
            icon="pi pi-arrow-left"
            iconPos="left"
            disabled={saving || subdomain.length < 3 || subdomainAvailable === false}
            onClick={this.saveSubdomain}
          />
        </div>
      </>
    );
  }

  renderMode() {
    const { mode, saving } = this.state;
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>{t("gym.setup.modeTitle")}</b>
              <p>{t("gym.setup.modeText")}</p>
            </div>
            <div className="gos-choices" style={{ "--d": "140ms" }}>
              {[["SOLO", "pi pi-user", "gym.trainers.solo", "gym.setup.soloText"], ["TEAM", "pi pi-users", "gym.trainers.team", "gym.setup.teamText"]].map(([key, icon, label, text]) => (
                <button type="button" key={key} className={`gos-choice ${mode === key ? "on" : ""}`} disabled={saving} onClick={() => this.chooseMode(key)}>
                  <i className={icon} aria-hidden="true"></i>
                  <b>{t(label)}</b>
                  <span>{t(text)}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="ob-composer">
          <Button className="back-button p-button-text" icon="pi pi-arrow-right" label={t("gym.setup.back")} onClick={() => this.go(1)} />
        </div>
      </>
    );
  }

  renderPlan() {
    const { plan, saving, plansDone } = this.state;
    const intervals = [
      { label: t("gym.plans.monthly"), value: 1 },
      { label: t("gym.plans.quarterly"), value: 3 },
      { label: t("gym.plans.yearly"), value: 12 },
    ];
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>{t("gym.setup.planTitle")}</b>
              <p>{t("gym.setup.planText")}</p>
            </div>
            <div className="bubble sys gos-form" style={{ "--d": "140ms" }}>
              <label>
                <span>{t("gym.plans.name")}</span>
                <InputText autoFocus value={plan.name} maxLength={120} placeholder={t("gym.setup.planNameHint")} onChange={(e) => this.setPlan({ name: e.target.value })} />
              </label>
              <div className="gos-pair">
                <label>
                  <span>{t("gym.plans.price")} (EGP)</span>
                  <InputText inputMode="decimal" dir="ltr" value={plan.price} onChange={(e) => this.setPlan({ price: e.target.value.replace(/[^0-9.]/g, "") })} />
                </label>
                <label>
                  <span>{t("gym.plans.interval")}</span>
                  <Dropdown options={intervals} value={plan.intervalMonths} onChange={(e) => this.setPlan({ intervalMonths: e.value })} />
                </label>
              </div>
              <label>
                <span>{t("gym.plans.trialDays")}</span>
                <InputText inputMode="numeric" dir="ltr" value={plan.trialDays} onChange={(e) => this.setPlan({ trialDays: e.target.value.replace(/[^0-9]/g, "") })} />
              </label>
            </div>
            {plansDone > 0 && (
              <div className="bubble me" style={{ "--d": "180ms" }}>
                <span className="tick">✓</span>
                <span className="txt">{t("gym.setup.planDone", { n: plansDone })}</span>
              </div>
            )}
          </>
        )}
        <div className="ob-composer">
          <Button className="back-button p-button-text" icon="pi pi-arrow-right" label={t("gym.setup.back")} onClick={() => this.go(2)} />
          {plansDone > 0 ? <Button className="p-button-text" label={t("gym.setup.skip")} onClick={() => this.go(4)} /> : null}
          <Button
            className="next-button activate"
            label={saving ? t("gym.setup.saving") : t("gym.setup.addPlan")}
            icon="pi pi-check"
            iconPos="left"
            disabled={saving}
            onClick={this.savePlan}
          />
        </div>
      </>
    );
  }

  renderProgram() {
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>{t("gym.setup.programTitle")}</b>
              <p>{t("gym.setup.programText")}</p>
            </div>
            <div className="bubble sys" style={{ "--d": "120ms" }}>
              <p>{t("gym.setup.readyText")}</p>
            </div>
          </>
        )}
        <div className="ob-composer">
          <Button className="p-button-text" label={t("gym.setup.later")} onClick={this.finish} />
          <Button
            className="next-button activate"
            label={t("gym.setup.addProgram")}
            icon="pi pi-plus"
            iconPos="left"
            onClick={() => {
              window.location.href = "/courses/add";
            }}
          />
        </div>
      </>
    );
  }

  render() {
    const { loading, step } = this.state;
    if (loading) {
      return (
        <div className="join-page onboarding-page loading">
          <div className="ob-loader" role="status">
            <span className="ring"></span>
            <b>{t("loading")}</b>
          </div>
        </div>
      );
    }
    const current = STEPS[step - 1];
    return (
      <div className="join-page onboarding-page">
        <Toast ref={this.toast}></Toast>
        <div className="sheet-wrap">
          <div className="sheet ob-chat">
            <div className="ob-bar">
              <span className="av" role="img" aria-label={properties.site_name} style={{ backgroundImage: `url(${LOGO})` }}></span>
              <span className="who">
                <b>{t("gym.setup.title")}</b>
                <small>{t("gym.setup.stepOf", { n: step, total: STEPS.length })} · {t(current.key)}</small>
              </span>
              <span className="dots" aria-hidden="true">
                {STEPS.map((s) => (
                  <i key={s.id} className={`${step > s.id ? "done" : ""} ${step === s.id ? "now" : ""}`} title={t(s.key)} onClick={() => (step > s.id ? this.go(s.id) : null)}></i>
                ))}
              </span>
            </div>
            {step === 1 && this.renderLink()}
            {step === 2 && this.renderMode()}
            {step === 3 && this.renderPlan()}
            {step === 4 && this.renderProgram()}
          </div>
        </div>
      </div>
    );
  }
}

export default GymOnboarding;
